import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import * as helpers from '../helpers';
import { dispatcher } from 'dispatcher';
import * as actions from '../store/actions';
import { useAfterLoginAction } from './useAfterLoginAction';
import { useFailedLoginAction } from './useFailedLoginAction';
import { useSuccessLoginAction } from './useSuccessLoginAction';
import { useGetWaitingMessages } from './useGetWaitingMessages';
import { useLoginBySessionToken } from './useLoginBySessionToken';
import { useRedirectToAfterLoginURL } from './useRedirectToAfterLoginURL';
import { useGetCredentialsLoginHooks } from './useGetCredentialsLoginHooks';
import { useAfterCredentialsLoginAction } from './useAfterCredentialsLoginAction';

export const useContinueLoginWithSessionToken = () => {
    const dispatch = useDispatch();
    const afterLoginAction = useAfterLoginAction();
    const failedLoginAction = useFailedLoginAction();
    const successLoginAction = useSuccessLoginAction();
    const getWaitingMessages = useGetWaitingMessages();
    const loginBySessionToken = useLoginBySessionToken();
    const redirectToAfterLoginURL = useRedirectToAfterLoginURL();
    const getCredentialsLoginHooks = useGetCredentialsLoginHooks();
    const afterCredentialsLoginAction = useAfterCredentialsLoginAction();

    return useCallback(async ({
        userId,
        userName,
        sessionToken,
        isAutoLogin,
        afterRegisterLogin,
        isLoginToNativeAppEnabled,
        isAfterLoginWithCredentials,
        ...rest
    }) => {
        try {
            if (isLoginToNativeAppEnabled) {
                try {
                    await helpers.externalLoginAction({ userId, userName, sessionToken, afterRegisterLogin, ...rest });
                } catch {
                    return;
                }
            }

            await loginBySessionToken(userName, sessionToken);

            if (isAfterLoginWithCredentials) {
                await getCredentialsLoginHooks();
            }

            await afterLoginAction();
            await successLoginAction({ userName, authType: 'credentials' });

            if (isAfterLoginWithCredentials) {
                dispatcher.dispatch('auth:credentials:login');

                afterCredentialsLoginAction();
            }

            await getWaitingMessages();

            redirectToAfterLoginURL(afterRegisterLogin);

            helpers.storeRememberMeItem(userName);
        } catch (error) {
            failedLoginAction(error);

            throw error;
        } finally {
            dispatch(actions.setIsLoginInProcess(false));
            isAutoLogin && dispatch(actions.setAutoLoginCompleted());
        }
    }, [
        dispatch,
        afterLoginAction,
        failedLoginAction,
        getWaitingMessages,
        successLoginAction,
        loginBySessionToken,
        redirectToAfterLoginURL,
        getCredentialsLoginHooks,
        afterCredentialsLoginAction
    ]);
};
